/*
  Titan Spirits - background/serve video helper
  - Videos marked [data-titan-video] (or a <video> directly inside [data-titan-video-wrap])
    play only while in view and pause when scrolled away.
  - Under prefers-reduced-motion: reduce they stay paused and native controls are shown.
  - Autoplaying videos without controls get an accessible pause/play toggle.
*/
(function () {
  if (window.titanVideoInit) return;
  window.titanVideoInit = true;

  var SELECTOR = 'video[data-titan-video], [data-titan-video-wrap] > video';
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var observer = null;

  function injectStyles() {
    if (document.getElementById('titan-video-styles')) return;
    var style = document.createElement('style');
    style.id = 'titan-video-styles';
    style.textContent =
      '.titan-video-toggle{position:absolute;right:12px;bottom:12px;z-index:3;display:inline-flex;' +
      'align-items:center;justify-content:center;width:44px;height:44px;padding:0;margin:0;' +
      'border:1px solid rgba(244,241,222,.6);border-radius:50%;background:rgba(13,27,42,.78);' +
      'color:#f4f1de;cursor:pointer;line-height:0;}' +
      '.titan-video-toggle:hover{background:rgba(13,27,42,.95);}' +
      '.titan-video-toggle:focus-visible{outline:2px solid #f4f1de;outline-offset:3px;}' +
      '.titan-video-toggle svg{width:16px;height:16px;fill:currentColor;}';
    document.head.appendChild(style);
  }

  var ICON_PAUSE = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect x="3" y="2" width="3.5" height="12"/><rect x="9.5" y="2" width="3.5" height="12"/></svg>';
  var ICON_PLAY = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4 2l10 6-10 6z"/></svg>';

  // Media events fire asynchronously, so flag script-initiated play/pause and
  // clear the flag in the event handler to tell them apart from visitor actions.
  function safePlay(video) {
    if (!video.paused) return;
    video._titanPendingPlay = true;
    var p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        video._titanPendingPlay = false;
        updateToggle(video);
      });
    }
  }

  function safePause(video) {
    if (video.paused) return;
    video._titanPendingPause = true;
    video.pause();
  }

  function updateToggle(video) {
    var btn = video._titanToggle;
    if (!btn) return;
    var playing = !video.paused;
    // Constant name + aria-pressed: pressed means "paused".
    btn.setAttribute('aria-pressed', playing ? 'false' : 'true');
    btn.innerHTML = playing ? ICON_PAUSE : ICON_PLAY;
  }

  function addToggle(video) {
    var parent = video.parentElement;
    if (!parent || video._titanToggle) return;
    if (window.getComputedStyle(parent).position === 'static') parent.style.position = 'relative';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'titan-video-toggle';
    btn.setAttribute('aria-label', 'Pause video');
    btn.addEventListener('click', function () {
      if (video.paused) {
        video._titanUserPaused = false;
        safePlay(video);
      } else {
        video._titanUserPaused = true;
        safePause(video);
      }
      updateToggle(video);
    });
    video._titanToggle = btn;
    parent.appendChild(btn);
    updateToggle(video);
  }

  function setup(video) {
    if (video._titanVideo) return;
    video._titanVideo = true;
    video.muted = true;

    // Track pauses/plays the visitor makes via native controls.
    video.addEventListener('pause', function () {
      if (video._titanPendingPause) {
        video._titanPendingPause = false;
      } else if (!video.ended) {
        video._titanUserPaused = true;
      }
      updateToggle(video);
    });
    video.addEventListener('play', function () {
      if (video._titanPendingPlay) {
        video._titanPendingPlay = false;
      } else {
        video._titanUserPaused = false;
      }
      updateToggle(video);
    });

    if (reducedMotion && reducedMotion.matches) {
      video.removeAttribute('autoplay');
      video.autoplay = false;
      safePause(video);
      video.controls = true;
      return;
    }

    // Every managed video plays automatically while visible, so any without
    // native controls needs a pause button (WCAG 2.2.2).
    if (!video.controls) addToggle(video);

    if (observer) {
      observer.observe(video);
    } else if (!video._titanUserPaused) {
      safePlay(video);
    }
  }

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      var video = entry.target;
      if (entry.isIntersecting) {
        if (!video._titanUserPaused) {
          if (video.preload === 'none') video.preload = 'auto';
          safePlay(video);
        }
      } else if (!video.paused) {
        safePause(video);
      }
      updateToggle(video);
    });
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(SELECTOR)) setup(root);
    Array.prototype.forEach.call(root.querySelectorAll(SELECTOR), setup);
  }

  function init() {
    injectStyles();
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(onIntersect, { threshold: 0.25 });
    }
    scan(document);

    // Cart and theme-editor re-renders can insert new videos.
    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          Array.prototype.forEach.call(m.addedNodes, function (node) {
            if (node.nodeType === 1) scan(node);
          });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }

    if (reducedMotion && reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', function (e) {
        if (!e.matches) return;
        Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), function (video) {
          if (observer) observer.unobserve(video);
          safePause(video);
          video.controls = true;
          if (video._titanToggle) {
            video._titanToggle.remove();
            video._titanToggle = null;
          }
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
